// Shared visual shell for every /reports/* page.
//
// Responsibilities:
//   - Gradient page header with title + subtitle + optional accent
//   - Breadcrumbs (passed through to PageContainer)
//   - ErrorBoundary wrapper so one broken report doesn't take down the shell
//   - Slot for filter bar, KPIs, body
//
// The three design principles from CLAUDE.md apply:
//   1. No Borders  — we use shadow + gradient backdrops, not hairline borders
//   2. Mono Data   — numeric content uses tabular-nums elsewhere
//   3. Glass Depth — header uses a gradient + soft inner glow

import type { ReactNode } from 'react';
import { PageContainer, ErrorBoundary } from '@bbpos/ui';

// The @bbpos/ui barrel exports two conflicting `BreadcrumbItem` types
// (AppShell uses `path`, PageContainer uses `href`). We need the PageContainer
// variant here, so declare it locally rather than importing the ambiguous
// symbol from the barrel.
interface BreadcrumbItem {
  label: string;
  href?: string;
}

export type ReportAccent =
  | 'brand-indigo' // Sales / default analytics
  | 'violet-pink' // Customer / Restaurant
  | 'warning-danger' // Expenses
  | 'teal-success' // Inventory / stock-linked reports
  | 'indigo-violet'; // BI / GST

const ACCENT_CLASSES: Record<ReportAccent, string> = {
  'brand-indigo': 'from-brand-600 via-brand-500 to-indigo-600',
  'violet-pink': 'from-violet-600 via-fuchsia-500 to-pink-500',
  'warning-danger': 'from-warning-500 via-orange-500 to-danger-500',
  'teal-success': 'from-teal-600 via-teal-500 to-success-600',
  'indigo-violet': 'from-indigo-600 via-violet-600 to-brand-600',
};

const ACCENT_SHADOW: Record<ReportAccent, string> = {
  'brand-indigo': 'shadow-[0_20px_60px_-20px_rgba(37,99,235,0.45)]',
  'violet-pink': 'shadow-[0_20px_60px_-20px_rgba(168,85,247,0.45)]',
  'warning-danger': 'shadow-[0_20px_60px_-20px_rgba(234,88,12,0.45)]',
  'teal-success': 'shadow-[0_20px_60px_-20px_rgba(13,148,136,0.45)]',
  'indigo-violet': 'shadow-[0_20px_60px_-20px_rgba(99,102,241,0.45)]',
};

// Header must NOT clip its own children: headerActions can host overlays (e.g.
// the Export dropdown) that open below the banner. The decorative glows are
// clipped by their own nested overflow-hidden wrapper instead, so the banner
// itself stays overflow-visible and never cuts off a menu.
export const REPORT_HEADER_CLASS =
  'relative rounded-2xl bg-gradient-to-br p-6 sm:p-7 text-white mb-6';

export interface ReportPageShellProps {
  title: string;
  subtitle: string;
  breadcrumbs: BreadcrumbItem[];
  accent?: ReportAccent;
  badge?: ReactNode;
  headerActions?: ReactNode;
  filterBar?: ReactNode;
  children: ReactNode;
  pageContainerTitle?: string;
}

export function ReportPageShell({
  title,
  subtitle,
  breadcrumbs,
  accent = 'brand-indigo',
  badge,
  headerActions,
  filterBar,
  children,
  pageContainerTitle,
}: ReportPageShellProps) {
  const gradient = ACCENT_CLASSES[accent];
  const shadow = ACCENT_SHADOW[accent];

  return (
    <PageContainer title={pageContainerTitle ?? ''} breadcrumbs={breadcrumbs}>
      <ErrorBoundary resetKey={`report-${title}`}>
        <div className="animate-fade-in">
          {/* Gradient header — Glass Depth */}
          <div className={`${REPORT_HEADER_CLASS} ${gradient} ${shadow}`}>
            {/* Decorative glow rings — clipped to the rounded header by their own
                wrapper so the header itself can stay overflow-visible (lets the
                Export dropdown open below the banner without being cut off). */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
              <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
              <div className="absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
            </div>

            <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                {badge ? <div className="mb-3">{badge}</div> : null}
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">{title}</h1>
                <p className="mt-1.5 text-sm sm:text-[0.95rem] text-white/85 max-w-xl">{subtitle}</p>
              </div>
              {headerActions ? (
                <div className="flex flex-wrap items-center gap-2 sm:justify-end">{headerActions}</div>
              ) : null}
            </div>
          </div>

          {filterBar ? <div className="mb-6">{filterBar}</div> : null}

          {children}
        </div>
      </ErrorBoundary>
    </PageContainer>
  );
}
